// Shared helpers for the Organigram change-log + Excel exports.
//
// There are two distinct Excel deliverables, both built from the same rows so
// they never diverge again:
//   1. The TASK REPORT  -> human readable, every change marked + a "Changes" sheet
//                          describing exactly what the client changed.
//   2. The IMPORT FILE  -> same U101 column contract as before so it still
//                          round-trips through processExcelSync(), with the
//                          change marking added on top.
//
// All styling uses xlsx-js-style (a drop-in superset of `xlsx`).
import XLSX from 'xlsx-js-style';

// --- Canonical change statuses (stored verbatim in the "Status" column so the
//     importer can detect them language-independently). ---
export const STATUS = { NEW: 'New', UPDATED: 'Updated', REMOVED: 'Removed' };

const LANG_MAP = { de: 'Deutsch', en: 'English', fr: 'Français', it: 'Italiano', es: 'Español' };

// The exact column order the import parser (processExcelSync) understands.
// "Status" / "Change Details" are appended; processExcelSync ignores unknown
// columns for everything except the removal detection below.
export const USER_COLUMNS = [
    'Person ID', 'Last Name', 'First Name', 'E-Mail',
    'Organisation ID', 'Organisation Name',
    'Superior Organisation ID', 'Superior Organisation Name',
    'Role', 'Gender', 'Birthday',
    'Superior ID', 'Superior Last Name', 'Superior First Name',
    'Language', 'Entry Date', 'Ist Vorgesetzter', 'Neueintritte',
    'Status', 'Change Details',
];

// Cell fills keyed by canonical status.
const STATUS_STYLE = {
    [STATUS.NEW]:     { fill: { patternType: 'solid', fgColor: { rgb: 'C6EFCE' } }, font: { color: { rgb: '006100' } } },
    [STATUS.UPDATED]: { fill: { patternType: 'solid', fgColor: { rgb: 'FFEB9C' } }, font: { color: { rgb: '9C6500' } } },
    [STATUS.REMOVED]: { fill: { patternType: 'solid', fgColor: { rgb: 'FFC7CE' } }, font: { color: { rgb: '9C0006' }, strike: true } },
};

const HEADER_STYLE = { font: { bold: true }, fill: { patternType: 'solid', fgColor: { rgb: 'EFEFEF' } } };

// --- Identity / normalization -------------------------------------------------

// Raw users (users.json) come straight from Azure and use givenName/surname/
// externalUserId, while the rest of the app expects firstName/lastName/externalId.
// Normalising here is what stops "undefined undefined" leaking into the chart,
// the drawer and the change log.
export const normalizeRawUser = (raw, base = {}) => {
    if (!raw) return { ...base };
    return {
        ...raw,
        firstName: raw.firstName || raw.givenName || base.firstName || '',
        lastName: raw.lastName || raw.surname || base.lastName || '',
        externalId: raw.externalId || raw.externalUserId || raw.external_id || base.externalId || '',
        mail: raw.mail || raw.email || base.mail || '',
        language: raw.language || raw.preferredLanguage || base.language || 'DE',
    };
};

// Never returns "undefined undefined": falls back through every name-ish field.
export const userDisplayName = (u) => {
    if (!u) return '';
    const full = `${u.firstName || u.givenName || ''} ${u.lastName || u.surname || ''}`.trim();
    return full || u.displayName || u.mail || u.email || u.externalId || u.externalUserId || '';
};

const keyOf = (v) => (v === undefined || v === null || v === '') ? null : String(v).toLowerCase();

// --- Change index -------------------------------------------------------------

// Walks the change log once and produces lookups the exporters use:
//   userStatus  : Map<key, {status, role, orgName, orgExtId, detail}>  keyed by
//                 internal id / externalId / mail (whichever the row exposes).
//   removedUsers: full snapshots of removed users, so they can be re-inserted
//                 into the sheet and marked (we never drop them).
//   orgStatus   : Map<orgId, {status, name, externalId, parentName}>.
export const buildChangeIndex = (changeLog = []) => {
    const userStatus = new Map();
    const removedUsers = [];
    const orgStatus = new Map();

    const findUserStatus = (keys) => {
        for (const k of keys) { const kk = keyOf(k); if (kk && userStatus.has(kk)) return userStatus.get(kk); }
        return null;
    };
    const setUserStatus = (keys, value) => {
        keys.forEach((k) => { const kk = keyOf(k); if (kk) userStatus.set(kk, value); });
    };

    changeLog.forEach((entry) => {
        const d = entry.details || {};
        const p = entry.params || {};
        const user = d.user || (d.exportContext && d.exportContext.user) || null;
        const org = d.org || null;
        const role = p.role || d.role || '';
        const orgName = (org && org.name) || p.org || '';
        const orgExtId = (org && (org.externalId || org.external_id)) || '';
        const keys = [d.userId, user && user.id, user && user.externalId, user && user.externalUserId, p.externalId, user && user.mail, user && user.email];

        switch (entry.type) {
            case 'USER_ADD':
            case 'EXCEL_NEW':
            case 'EXCEL_ADD_ROLE':
                setUserStatus(keys, { status: STATUS.NEW, role, orgName, orgExtId, detail: entry });
                break;
            case 'USER_UPDATE':
            case 'EXCEL_UPDATE': {
                const existing = findUserStatus(keys);
                if (!existing || existing.status !== STATUS.NEW) {
                    setUserStatus(keys, { status: STATUS.UPDATED, role, orgName, orgExtId, detail: entry });
                }
                break;
            }
            case 'USER_REMOVE': {
                setUserStatus(keys, { status: STATUS.REMOVED, role, orgName, orgExtId, detail: entry });
                const snapshot = user || {
                    id: d.userId,
                    firstName: (p.name || '').split(' ')[0] || '',
                    lastName: (p.name || '').split(' ').slice(1).join(' ') || '',
                    externalId: p.externalId || '',
                };
                removedUsers.push({ user: snapshot, orgName, orgExtId, role, entry });
                break;
            }
            case 'ORG_ADD':
                orgStatus.set(d.orgId, { status: STATUS.NEW, name: p.name || '', externalId: d.externalId || '', parentName: d.parentName || p.parent || '' });
                break;
            case 'ORG_UPDATE': {
                const prev = orgStatus.get(d.orgId);
                if (!prev || prev.status !== STATUS.NEW) {
                    orgStatus.set(d.orgId, { status: STATUS.UPDATED, name: p.name || '', externalId: (d.diff && d.diff.externalId) || '' });
                }
                break;
            }
            case 'ORG_DELETE':
                orgStatus.set(d.orgId, { status: STATUS.REMOVED, name: p.name || '', externalId: '' });
                break;
            default:
                break;
        }
    });

    return { userStatus, removedUsers, orgStatus };
};

export const lookupUserStatus = (userStatus, u) => {
    if (!userStatus || !u) return null;
    const keys = [u.id, u.externalId, u.externalUserId, u.mail, u.email, u['Person ID'], u['E-Mail']];
    for (const k of keys) { const kk = keyOf(k); if (kk && userStatus.has(kk)) return userStatus.get(kk); }
    return null;
};

// --- Row building -------------------------------------------------------------

const langLabel = (u) => {
    const code = (u.preferredLanguage || u.language || '').toString().toLowerCase().slice(0, 2);
    return LANG_MAP[code] || u.preferredLanguage || u.language || '';
};

// Turns an internal role key (leaders/mitarbeiter/viewers) into a readable label.
export const roleLabel = (role, t) => {
    if (!role) return '';
    const key = `organigram_ui.role_${role}`;
    const label = t ? t('common', key) : key;
    return label === key ? role : label;
};

// Human, localized one-liner describing a single change-index hit.
const describeStatus = (st, t, ns = 'common') => {
    if (!st) return '';
    const tr = (key, params) => (t ? t(ns, key, params) : key);
    const role = roleLabel(st.role, t);
    if (st.status === STATUS.REMOVED) return tr('export.detail_removed', { org: st.orgName || '', role });
    if (st.status === STATUS.NEW) return tr('export.detail_new', { org: st.orgName || '', role });
    const diff = st.detail && st.detail.details && st.detail.details.formattedDiff;
    return diff ? tr('export.detail_updated', { diff }) : tr('export.detail_updated_generic', {});
};

const userToRow = (u, org, parentOrg, st, t) => ({
    'Person ID': u.externalId || u.externalUserId || '',
    'Last Name': u.lastName || u.surname || '',
    'First Name': u.firstName || u.givenName || '',
    'E-Mail': u.mail || u.email || '',
    'Organisation ID': (org && org.externalId) || '',
    'Organisation Name': (org && org.name) || '',
    'Superior Organisation ID': (parentOrg && parentOrg.externalId) || '',
    'Superior Organisation Name': (parentOrg && parentOrg.name) || '',
    'Role': u.jobTitle || '',
    'Gender': u.gender || '',
    'Birthday': u.birthday || '',
    'Superior ID': u.derivedSupExternalId || '',
    'Superior Last Name': u.derivedSupLastName || '',
    'Superior First Name': u.derivedSupFirstName || '',
    'Language': langLabel(u),
    'Entry Date': u.entryDate || '',
    'Ist Vorgesetzter': (org && org.lists && org.lists.leaders && org.lists.leaders.some((l) => l.id === u.id)) ? 'J' : 'N',
    'Neueintritte': st && st.status === STATUS.NEW ? 'Ja' : 'Nein',
    'Status': st ? st.status : '',
    'Change Details': describeStatus(st, t),
});

// Builds the user rows + a parallel statuses array (used for cell fills).
// opts: { includeUnassigned, includeRemoved }
export const buildUserRows = ({ chartData = [], allRawUsers = {}, changeIndex, t, includeUnassigned = false, includeRemoved = true } = {}) => {
    const idx = changeIndex || buildChangeIndex([]);
    const orgMap = chartData.reduce((acc, d) => { acc[d.id] = d; return acc; }, {});
    const rows = [];
    const statuses = [];
    const exportedIds = new Set();

    chartData.forEach((d) => {
        // Viewers are excluded on purpose: the importer ignores them, and
        // emitting viewer-only rows would make re-import treat them as new users.
        const list = [...(d.lists?.leaders || []), ...(d.lists?.mitarbeiter || [])];
        const parentId = d.pid || d.stpid || d.parent_id || d.parentId;
        const parentOrg = parentId ? orgMap[parentId] : null;

        list.forEach((u) => {
            exportedIds.add(u.id);
            const st = lookupUserStatus(idx.userStatus, u);
            rows.push(userToRow(u, d, parentOrg, st, t));
            statuses.push(st ? st.status : null);
        });
    });

    if (includeUnassigned && allRawUsers) {
        Object.values(allRawUsers).forEach((raw) => {
            if (exportedIds.has(raw.id)) return;
            if (raw.disabled || raw.isDisabled) return;
            const u = normalizeRawUser(raw);
            const st = lookupUserStatus(idx.userStatus, u);
            rows.push(userToRow(u, null, null, st, t));
            statuses.push(st ? st.status : null);
        });
    }

    // Deleted users are gone from chartData; re-insert them so the sheet keeps
    // every change visible (marked Removed / red-strikethrough).
    if (includeRemoved) {
        idx.removedUsers.forEach(({ user, orgName, orgExtId, role }) => {
            const u = normalizeRawUser(user);
            if (exportedIds.has(u.id)) return; // still present elsewhere -> already shown
            const pseudoOrg = { name: orgName, externalId: orgExtId, lists: { leaders: role === 'leaders' ? [u] : [] } };
            rows.push(userToRow(u, pseudoOrg, null, { status: STATUS.REMOVED, orgName, role }, t));
            statuses.push(STATUS.REMOVED);
        });
    }

    return { rows, statuses };
};

// --- Worksheet helpers --------------------------------------------------------

export const styledUserSheet = (rows, statuses) => {
    const ws = XLSX.utils.json_to_sheet(rows, { header: USER_COLUMNS });
    const range = XLSX.utils.decode_range(ws['!ref']);

    // Header row styling.
    for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[addr]) ws[addr].s = { ...(ws[addr].s || {}), ...HEADER_STYLE };
    }
    // Per-row status fills.
    statuses.forEach((status, i) => {
        const style = status && STATUS_STYLE[status];
        if (!style) return;
        const r = i + 1; // +1 for header
        for (let c = range.s.c; c <= range.e.c; c++) {
            const addr = XLSX.utils.encode_cell({ r, c });
            if (!ws[addr]) ws[addr] = { t: 's', v: '' };
            ws[addr].s = { ...(ws[addr].s || {}), ...style };
        }
    });
    ws['!cols'] = USER_COLUMNS.map((k) => ({ wch: Math.max(12, k.length + 2) }));
    return ws;
};

// One-row-per-change sheet for the human task report: this is the "know exactly
// which change the client made" deliverable.
export const buildChangesSheet = (changeLog = [], chartData = [], t) => {
    const orgMap = chartData.reduce((acc, d) => { acc[d.id] = d; return acc; }, {});
    const tr = (key, params) => (t ? t('common', key, params) : key);

    const header = ['#', 'Type', 'Person', 'Person ID', 'Organisation', 'Organisation ID', 'Role', 'Status', 'Description', 'When'];
    const aoa = [header];
    const styleRows = [];

    changeLog.forEach((c, i) => {
        const d = c.details || {};
        const p = c.params || {};
        const user = d.user || (d.exportContext && d.exportContext.user) || null;
        // Resolve the org's *current* name/id by id (so a renamed "New Department"
        // shows its real name), falling back to whatever the entry captured.
        const liveOrg = d.orgId ? orgMap[d.orgId] : null;
        const orgName = (liveOrg && liveOrg.name) || (d.org && d.org.name) || p.org || p.name || '';
        const orgExtId = (liveOrg && liveOrg.externalId) || (d.org && d.org.externalId) || '';
        const typeLabel = tr(`types.${(c.type || '').toLowerCase()}`);
        const isUser = (c.type || '').startsWith('USER') || (c.type || '').startsWith('EXCEL');
        const status = /REMOVE|DELETE/.test(c.type) ? STATUS.REMOVED
            : /ADD|NEW/.test(c.type) ? STATUS.NEW : STATUS.UPDATED;
        const desc = c.key ? tr(c.key, p) : (c.desc || '');
        const diff = d.formattedDiff ? ` (${d.formattedDiff})` : '';

        aoa.push([
            i + 1,
            typeLabel,
            isUser ? (userDisplayName(user) || p.name || '') : '',
            (user && (user.externalId || user.externalUserId)) || '',
            isUser ? orgName : (p.name || orgName),
            orgExtId,
            p.role || '',
            status,
            `${desc}${diff}`.trim(),
            c.timestamp ? String(c.timestamp).replace('T', ' ').slice(0, 19) : '',
        ]);
        styleRows.push(status);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[addr]) ws[addr].s = { ...(ws[addr].s || {}), ...HEADER_STYLE };
    }
    styleRows.forEach((status, i) => {
        const style = STATUS_STYLE[status];
        if (!style) return;
        const r = i + 1;
        for (let c = range.s.c; c <= range.e.c; c++) {
            const addr = XLSX.utils.encode_cell({ r, c });
            if (ws[addr]) ws[addr].s = { ...(ws[addr].s || {}), ...style };
        }
    });
    ws['!cols'] = [4, 18, 22, 12, 24, 14, 12, 10, 48, 18].map((w) => ({ wch: w }));
    return ws;
};

// --- Public builders ----------------------------------------------------------

// Importable round-trip file: same columns + all users (incl. unassigned), with
// change marking layered on. Filename kept generic for the downstream tool.
export const buildImportWorkbook = ({ chartData, allRawUsers, changeLog, t }) => {
    const changeIndex = buildChangeIndex(changeLog);
    const { rows, statuses } = buildUserRows({ chartData, allRawUsers, changeIndex, t, includeUnassigned: true, includeRemoved: true });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, styledUserSheet(rows, statuses), 'Getimpulse-Page1-Komponente1');
    return wb;
};

// Human task report: focused user sheet (no unassigned noise) + a Changes sheet.
export const buildReportWorkbook = ({ chartData, changeLog, t }) => {
    const changeIndex = buildChangeIndex(changeLog);
    const { rows, statuses } = buildUserRows({ chartData, changeIndex, t, includeUnassigned: false, includeRemoved: true });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, buildChangesSheet(changeLog, chartData, t), 'Changes');
    XLSX.utils.book_append_sheet(wb, styledUserSheet(rows, statuses), 'Users');
    return wb;
};

export const workbookToBlob = (wb) => {
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

// Writes a styled workbook to disk. Must go through xlsx-js-style (not the plain
// `xlsx` writeFile) or the cell fills/strikethroughs are silently dropped.
export const writeWorkbook = (wb, filename) => XLSX.writeFile(wb, filename);

// True when an imported row was marked as a removal in a previous export.
export const isRemovalRow = (row) => {
    const s = (row && (row['Status'] || row['Änderung'] || row['Change'])) || '';
    return /remov|entfernt|gel(ö|oe)sch|delete/i.test(String(s));
};

export default {
    STATUS, USER_COLUMNS, normalizeRawUser, userDisplayName, roleLabel,
    buildChangeIndex, lookupUserStatus, buildUserRows,
    styledUserSheet, buildChangesSheet,
    buildImportWorkbook, buildReportWorkbook, workbookToBlob, writeWorkbook, isRemovalRow,
};
